/**
 * AudioHelper.ts
 * Web Audio API utilities for decoding, waveform rendering, 7-second trimming,
 * lossless WAV encoding, and pre-curated royalty-free Reels music synthesis.
 */

export interface CuratedTrack {
  id: string;
  name: string;
  genre: string;
  bpm: number;
  description: string;
  icon: string;
}

export interface TrendingTrack {
  id: string;
  title: string;
  artist: string;
  album: string;
  artwork: string;
  previewUrl: string;
  genre: string;
  durationMs: number;
}

export interface TrendingCategory {
  id: string;
  label: string;
  icon: string;
  searchQuery: string;
}

export class AudioHelper {
  private static audioCtx: AudioContext | null = null;

  public static getAudioContext(): AudioContext {
    if (!this.audioCtx || this.audioCtx.state === 'closed') {
      const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
      this.audioCtx = new AudioCtxClass();
    }
    if (this.audioCtx.state === 'suspended') {
      this.audioCtx.resume();
    }
    return this.audioCtx;
  }

  /**
   * Decodes an ArrayBuffer (e.g. from File or fetch) into an AudioBuffer
   */
  public static async decodeAudio(arrayBuffer: ArrayBuffer): Promise<AudioBuffer> {
    const ctx = this.getAudioContext();
    return await ctx.decodeAudioData(arrayBuffer.slice(0));
  }

  /**
   * Decodes an audio file directly from a File object
   */
  public static async decodeAudioFile(file: File): Promise<AudioBuffer> {
    const arrayBuffer = await file.arrayBuffer();
    return await this.decodeAudio(arrayBuffer);
  }

  /**
   * Fetches a remote audio stream (e.g. iTunes preview) and decodes into AudioBuffer
   */
  public static async fetchAndDecodeAudio(url: string): Promise<AudioBuffer> {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to download audio preview: ${response.statusText}`);
    }
    const arrayBuffer = await response.arrayBuffer();
    return await this.decodeAudio(arrayBuffer);
  }

  /**
   * Pre-configured trending category quick-filters matching Instagram Reels & TikTok viral trends
   */
  public static getTrendingCategories(): TrendingCategory[] {
    return [
      {
        id: 'viral',
        label: 'Viral Reels Hits',
        icon: '🔥',
        searchQuery: 'top hits viral reels'
      },
      {
        id: 'halloween',
        label: 'Halloween & Spooky',
        icon: '🎃',
        searchQuery: 'halloween spooky party'
      },
      {
        id: 'party',
        label: '360 Booth & Party',
        icon: '🪩',
        searchQuery: 'party dance hype club'
      },
      {
        id: 'wedding',
        label: 'Wedding & Romance',
        icon: '💍',
        searchQuery: 'romantic wedding acoustic love'
      },
      {
        id: 'phonk',
        label: 'Cyber Phonk & Drift',
        icon: '⚡',
        searchQuery: 'phonk drift'
      },
      {
        id: 'latin',
        label: 'Latin & Dembow',
        icon: '🌴',
        searchQuery: 'latin reggaeton dembow'
      },
      {
        id: 'indie',
        label: 'Indie & Golden Hour',
        icon: '🎸',
        searchQuery: 'indie pop upbeat feel good'
      },
      {
        id: 'lofi',
        label: 'Aesthetic Lo-Fi',
        icon: '☕',
        searchQuery: 'lofi hip hop chill beats'
      }
    ];
  }

  /**
   * Search trending audio tracks via the public Apple iTunes search API with CORS support
   */
  public static async searchTrendingAudio(query: string, limit: number = 24): Promise<TrendingTrack[]> {
    const cleanQuery = query.trim() || 'trending viral reels';
    const url = `https://itunes.apple.com/search?term=${encodeURIComponent(cleanQuery)}&entity=song&limit=${limit}`;

    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`Search request failed with status ${res.status}`);
    }

    const data = await res.json();
    if (!data.results || !Array.isArray(data.results)) {
      return [];
    }

    const tracks: TrendingTrack[] = [];
    for (const item of data.results) {
      if (!item.previewUrl) continue;

      // Enhance artwork to higher resolution (300x300 instead of 100x100)
      const artwork = (item.artworkUrl100 || item.artworkUrl60 || '')
        .replace('100x100bb', '300x300bb')
        .replace('60x60bb', '300x300bb');

      tracks.push({
        id: String(item.trackId || Math.random()),
        title: item.trackName || 'Untitled Track',
        artist: item.artistName || 'Unknown Artist',
        album: item.collectionName || '',
        artwork,
        previewUrl: item.previewUrl,
        genre: item.primaryGenreName || 'Music',
        durationMs: item.trackTimeMillis || 30000
      });
    }

    return tracks;
  }

  /**
   * Extracts an exact 7.00-second slice from an AudioBuffer starting at `startTime`
   */
  public static sliceAudio(
    sourceBuffer: AudioBuffer,
    startTime: number,
    duration: number = 7.0,
    fadeIn: boolean = true,
    fadeOut: boolean = true
  ): AudioBuffer {
    const ctx = this.getAudioContext();
    const sampleRate = sourceBuffer.sampleRate;
    const channels = sourceBuffer.numberOfChannels;
    const targetLength = Math.round(duration * sampleRate);
    const startSample = Math.max(0, Math.min(Math.round(startTime * sampleRate), sourceBuffer.length - 1));

    const sliced = ctx.createBuffer(channels, targetLength, sampleRate);

    const fadeSampleCountIn = fadeIn ? Math.round(0.15 * sampleRate) : 0;
    const fadeSampleCountOut = fadeOut ? Math.round(0.25 * sampleRate) : 0;

    for (let c = 0; c < channels; c++) {
      const srcData = sourceBuffer.getChannelData(c);
      const dstData = sliced.getChannelData(c);

      for (let i = 0; i < targetLength; i++) {
        const srcIdx = startSample + i;
        let sample = srcIdx < sourceBuffer.length ? srcData[srcIdx] : 0;

        // Apply smooth fade-in
        if (fadeIn && i < fadeSampleCountIn) {
          sample *= i / fadeSampleCountIn;
        }

        // Apply smooth fade-out
        if (fadeOut && i > targetLength - fadeSampleCountOut) {
          sample *= (targetLength - i) / fadeSampleCountOut;
        }

        dstData[i] = sample;
      }
    }

    return sliced;
  }

  /**
   * Encodes an AudioBuffer into standard 16-bit PCM stereo/mono WAV format
   */
  public static audioBufferToWavBlob(buffer: AudioBuffer): Blob {
    const numChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;
    const bytesPerSample = bitDepth / 8;
    const blockAlign = numChannels * bytesPerSample;

    const numSamples = buffer.length;
    const dataSize = numSamples * blockAlign;
    const headerSize = 44;
    const totalSize = headerSize + dataSize;

    const arrayBuffer = new ArrayBuffer(totalSize);
    const view = new DataView(arrayBuffer);

    // RIFF chunk descriptor
    this.writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    this.writeString(view, 8, 'WAVE');

    // "fmt " sub-chunk
    this.writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true); // Subchunk1Size (16 for PCM)
    view.setUint16(20, format, true); // AudioFormat (1)
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true); // ByteRate
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);

    // "data" sub-chunk
    this.writeString(view, 36, 'data');
    view.setUint32(40, dataSize, true);

    // Interleave channels & write 16-bit signed PCM
    let offset = 44;
    const channelsData: Float32Array[] = [];
    for (let c = 0; c < numChannels; c++) {
      channelsData.push(buffer.getChannelData(c));
    }

    for (let i = 0; i < numSamples; i++) {
      for (let c = 0; c < numChannels; c++) {
        let sample = channelsData[c][i];
        // Clip to [-1.0, 1.0]
        sample = Math.max(-1, Math.min(1, sample));
        // Scale to 16-bit signed integer [-32768, 32767]
        const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
        view.setInt16(offset, intSample, true);
        offset += 2;
      }
    }

    return new Blob([arrayBuffer], { type: 'audio/wav' });
  }

  private static writeString(view: DataView, offset: number, string: string) {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }

  /**
   * Pre-curated list of trending Reels / Photobooth hooks
   */
  public static getCuratedTracks(): CuratedTrack[] {
    return [
      {
        id: 'spooky_bounce',
        name: 'Trunk & Treat Spooky Trap',
        genre: 'Halloween / Party',
        bpm: 135,
        description: 'Bouncing 808s, eerie bells & organ hits — perfect for Halloween & 360 booths',
        icon: '🎃'
      },
      {
        id: 'vip_glamour',
        name: 'VIP Red Carpet House',
        genre: 'Dance / Celebration',
        bpm: 126,
        description: 'Crisp house drums, filtered chords & celebratory upbeat energy',
        icon: '🥂'
      },
      {
        id: 'cyber_phonk',
        name: 'Neon Drift Phonk',
        genre: 'Viral Reels / Phonk',
        bpm: 140,
        description: 'Distorted cowbell hook, heavy sliding 808 bass, high-velocity reel vibe',
        icon: '⚡'
      },
      {
        id: 'funk_groove',
        name: 'Retro 80s Disco Funk',
        genre: 'Funk / Feel-Good',
        bpm: 120,
        description: 'Slap bassline, punchy brass stabs and infectious party groove',
        icon: '🪩'
      },
      {
        id: 'latin_reggaeton',
        name: 'Dembow Fiesta Beat',
        genre: 'Latin / Club',
        bpm: 104,
        description: 'Punchy Latin dembow rhythm, synth trumpet riffs & vibrant carnival energy',
        icon: '🌴'
      },
      {
        id: 'indie_sunset',
        name: 'Golden Hour Indie Pop',
        genre: 'Acoustic / Upbeat',
        bpm: 118,
        description: 'Rhythmic acoustic strums, clap beat & bright melodic whistle hook',
        icon: '🎸'
      },
      {
        id: 'lofi_chill',
        name: 'Velvet Midnight Chill',
        genre: 'Lo-Fi / Aesthetic',
        bpm: 86,
        description: 'Warm vinyl crackle, gentle jazz keys & smooth relaxed hip-hop drums',
        icon: '☕'
      }
    ];
  }

  /**
   * Procedurally synthesizes a high-quality 7-second musical hook right in the browser
   * using the Web Audio API. 100% offline, zero latency, royalty-free!
   */
  public static synthesizeCuratedTrack(trackId: string): AudioBuffer {
    const ctx = this.getAudioContext();
    const sampleRate = ctx.sampleRate;
    const duration = 7.0; // Exact 7-second reel length
    const totalSamples = Math.round(duration * sampleRate);
    const buffer = ctx.createBuffer(2, totalSamples, sampleRate);
    const left = buffer.getChannelData(0);
    const right = buffer.getChannelData(1);

    const track = this.getCuratedTracks().find(t => t.id === trackId) || this.getCuratedTracks()[0];
    const bpm = track.bpm;
    const beatSec = 60 / bpm;

    // Helper: add sine/saw oscillator note into buffer
    const addSynthNote = (
      startSec: number,
      noteDur: number,
      freq: number,
      type: 'sine' | 'sawtooth' | 'triangle' | 'square',
      vol: number = 0.25,
      pan: number = 0
    ) => {
      const startS = Math.round(startSec * sampleRate);
      const endS = Math.min(totalSamples, Math.round((startSec + noteDur) * sampleRate));

      for (let s = startS; s < endS; s++) {
        const t = (s - startS) / sampleRate;
        const phase = t * freq * 2 * Math.PI;
        let wave = 0;

        if (type === 'sine') wave = Math.sin(phase);
        else if (type === 'triangle') wave = Math.asin(Math.sin(phase)) * (2 / Math.PI);
        else if (type === 'square') wave = Math.sin(phase) >= 0 ? 1 : -1;
        else if (type === 'sawtooth') wave = 2 * ((t * freq) % 1) - 1;

        // Envelope: fast attack, exponential decay
        const env = Math.exp(-t * (4 / noteDur));
        const val = wave * env * vol;

        const panL = Math.cos(((pan + 1) * Math.PI) / 4);
        const panR = Math.sin(((pan + 1) * Math.PI) / 4);

        left[s] += val * panL;
        right[s] += val * panR;
      }
    };

    // Helper: add punchy kick drum
    const addKick = (startSec: number, vol: number = 0.6) => {
      const startS = Math.round(startSec * sampleRate);
      const len = Math.round(0.28 * sampleRate);
      for (let i = 0; i < len; i++) {
        const s = startS + i;
        if (s >= totalSamples) break;
        const t = i / sampleRate;
        // Pitch drop from 140Hz down to 45Hz
        const f = 45 + 95 * Math.exp(-t * 28);
        const env = Math.exp(-t * 12);
        const val = Math.sin(t * f * 2 * Math.PI) * env * vol;
        left[s] += val;
        right[s] += val;
      }
    };

    // Helper: add snare / clap
    const addSnare = (startSec: number, vol: number = 0.35) => {
      const startS = Math.round(startSec * sampleRate);
      const len = Math.round(0.22 * sampleRate);
      for (let i = 0; i < len; i++) {
        const s = startS + i;
        if (s >= totalSamples) break;
        const t = i / sampleRate;
        // Tone + Noise
        const tone = Math.sin(t * 180 * 2 * Math.PI) * Math.exp(-t * 22);
        const noise = (Math.random() * 2 - 1) * Math.exp(-t * 16);
        const val = (tone * 0.4 + noise * 0.6) * vol;
        left[s] += val * 0.95;
        right[s] += val * 0.95;
      }
    };

    // Helper: add crisp hi-hat
    const addHat = (startSec: number, open: boolean = false, vol: number = 0.15) => {
      const startS = Math.round(startSec * sampleRate);
      const len = Math.round((open ? 0.2 : 0.05) * sampleRate);
      for (let i = 0; i < len; i++) {
        const s = startS + i;
        if (s >= totalSamples) break;
        const t = i / sampleRate;
        const noise = (Math.random() * 2 - 1) * Math.exp(-t * (open ? 18 : 65));
        const val = noise * vol;
        left[s] += val * 0.7;
        right[s] += val * 1.0;
      }
    };

    // Compose tracks based on track ID
    if (trackId === 'spooky_bounce') {
      // Halloween / Spooky Theme: Eerie bells, 808 bounce
      const notes = [440, 523.25, 493.88, 440, 392, 440, 523.25, 659.25]; // Am spooky theme
      for (let t = 0; t < duration; t += beatSec) {
        const beatNum = Math.floor(t / beatSec);
        if (beatNum % 4 === 0) addKick(t, 0.7);
        if (beatNum % 4 === 2) addSnare(t, 0.4);
        addHat(t, false, 0.12);
        addHat(t + beatSec / 2, true, 0.08);

        // Gothic Bell melody
        const noteFreq = notes[beatNum % notes.length];
        addSynthNote(t, beatSec * 0.9, noteFreq, 'triangle', 0.28, Math.sin(beatNum));
        // Sub bass
        if (beatNum % 2 === 0) {
          addSynthNote(t, beatSec * 1.8, noteFreq / 4, 'sine', 0.5, 0);
        }
      }
    } else if (trackId === 'vip_glamour') {
      // 4-on-the-floor House Beat with upbeat chords
      const chords = [
        [523.25, 659.25, 783.99], // C
        [587.33, 698.46, 880.0],  // Dm
        [659.25, 783.99, 987.77], // Em
        [698.46, 880.0, 1046.5]   // F
      ];
      for (let t = 0; t < duration; t += beatSec) {
        const beatNum = Math.floor(t / beatSec);
        addKick(t, 0.65); // 4 on floor
        if (beatNum % 2 === 1) addSnare(t, 0.35);
        addHat(t + beatSec / 2, true, 0.16); // Off-beat hi-hat

        // Chord stabs
        const chord = chords[Math.floor(beatNum / 4) % chords.length];
        chord.forEach((f, idx) => {
          addSynthNote(t, beatSec * 0.45, f, 'sawtooth', 0.12, (idx - 1) * 0.5);
        });
        // House bassline
        addSynthNote(t + beatSec * 0.5, beatSec * 0.4, chord[0] / 4, 'sine', 0.45, 0);
      }
    } else if (trackId === 'cyber_phonk') {
      // Phonk cowbells & sliding bass
      const cowbellNotes = [587.33, 587.33, 698.46, 587.33, 880.0, 783.99, 698.46, 659.25];
      for (let t = 0; t < duration; t += beatSec) {
        const beatNum = Math.floor(t / beatSec);
        if (beatNum % 2 === 0) addKick(t, 0.75);
        if (beatNum % 2 === 1) addSnare(t, 0.45);
        addHat(t, false, 0.18);
        addHat(t + beatSec / 2, false, 0.18);

        // Phonk cowbell hook
        const f = cowbellNotes[beatNum % cowbellNotes.length];
        addSynthNote(t, beatSec * 0.35, f, 'square', 0.22, 0.2);
        // Distorted 808
        if (beatNum % 2 === 0) {
          addSynthNote(t, beatSec * 1.5, f / 8, 'sawtooth', 0.38, 0);
        }
      }
    } else {
      // Default Groovy Party Hook
      for (let t = 0; t < duration; t += beatSec) {
        const beatNum = Math.floor(t / beatSec);
        if (beatNum % 4 === 0 || beatNum % 4 === 3) addKick(t, 0.65);
        if (beatNum % 4 === 2) addSnare(t, 0.4);
        addHat(t, false, 0.14);
        addHat(t + beatSec / 2, true, 0.1);

        const notes = [261.63, 329.63, 392.0, 493.88, 523.25];
        const f = notes[beatNum % notes.length];
        addSynthNote(t, beatSec * 0.6, f, 'triangle', 0.25, 0);
        addSynthNote(t, beatSec * 0.8, f / 2, 'sine', 0.4, 0);
      }
    }

    // Master normalize output to avoid clipping
    let maxAmp = 0.001;
    for (let i = 0; i < totalSamples; i++) {
      if (Math.abs(left[i]) > maxAmp) maxAmp = Math.abs(left[i]);
      if (Math.abs(right[i]) > maxAmp) maxAmp = Math.abs(right[i]);
    }
    const norm = 0.92 / maxAmp;
    for (let i = 0; i < totalSamples; i++) {
      left[i] *= norm;
      right[i] *= norm;
    }

    return buffer;
  }
}
